import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS, useRequestPermissionUpgradeMutation} from "@/api/companyApi";
import {USER_KEYS} from "@/api/userApi";
import Button from "@/components/Common/Button";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useLogin} from "@/hooks/useLogin";
import {useSelectedCompanyId, useUserPermission} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import UserAvatar from "@/pages/profile/UserAvatar";
import CredentialsStep from "@/pages/registerCompany/steps/CredentialsStep";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {editCompanyMemberModalAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import type {TPermission} from "@/store/types";
import useUserStore from "@/store/user";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useAtom} from "jotai";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useSearchParams} from "react-router";
import {useShallow} from "zustand/shallow";

const AccountEdit: FC = () => {
  const {t} = useTranslation("multi_account");
  const [searchParams] = useSearchParams();
  const agencyIdFromUrl = searchParams.get("agency_id");
  const companyIdFromUrl = searchParams.get("company_id");
  const selectedCompanyId = useSelectedCompanyId();
  const isAgency = !!agencyIdFromUrl;
  const {refetch: refetchUser} = useFetchUser();
  const loggedInUser = useUserStore(state => state.user);
  const userPermission = useUserPermission();
  const [editCompanyMemberModal, setEditCompanyMemberModal] = useAtom(editCompanyMemberModalAtom);
  const permissionFromUrl = (searchParams.get("permission") as TPermission) ?? "READ";
  const currentPermission = useMemo(
    () => (editCompanyMemberModal.isRegistering ? permissionFromUrl : userPermission),
    [editCompanyMemberModal.isRegistering, permissionFromUrl, userPermission],
  );
  const excludedPermissions = useMemo((): TPermission[] => {
    if (userPermission === "READ") return ["READ"];
    if (userPermission === "PURCHASE") return ["READ", "PURCHASE"];
    return [];
  }, [userPermission]);
  const {login, isPending: isLoggingIn} = useLogin();
  const validateCredentials = useRegistrationStore(state => state.validateCredentials);
  const [email, firstName, lastName, password] = useRegistrationStore(
    useShallow(state => [state.email, state.firstName, state.lastName, state.password]),
  );
  const [logo, setLogo] = useState<File | null>(null);
  const [isRequestPermissionFormVisible, setIsRequestPermissionFormVisible] = useState(false);
  const [permissionToRequest, setPermissionToRequest] = useState<TPermission | null>(null);

  const {mutate: requestPermissionUpgrade, isPending: isRequestingPermissionUpgrade} =
    useRequestPermissionUpgradeMutation({
      companyId: selectedCompanyId!,
      onSuccessCallback: () => setIsRequestPermissionFormVisible(false),
    });
  const {mutate: register, isPending: isRegistering} = useMutation({
    mutationKey: isAgency
      ? AGENCY_KEYS.mAgencyMemberRegistration(Number(agencyIdFromUrl))
      : COMPANY_KEYS.mCompanyMemberRegistration(Number(companyIdFromUrl)),
    mutationFn: () => {
      const formData = new FormData();
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      formData.append("email", email);
      formData.append("password", password);
      formData.append("username", email);
      formData.append("token", searchParams.get("token")!);

      if (!!logo) {
        formData.append("profile_picture", logo);
      }

      return client.post(
        isAgency
          ? AGENCY_KEYS.mAgencyMemberRegistration(Number(agencyIdFromUrl))[0]
          : COMPANY_KEYS.mCompanyMemberRegistration(Number(companyIdFromUrl))[0],
        formData,
        {
          headers: {"Content-Type": "multipart/form-data"},
        },
      );
    },
    onSuccess: () => {
      login({email, password});
      setEditCompanyMemberModal({isVisible: false, isRegistering: false});
    },
    onError: (error: AxiosError<{error: string}>) => handleErrorWithToast(error),
  });
  const {mutate: updateUser, isPending: isUpdatingUser} = useMutation({
    mutationKey: USER_KEYS.mUser(loggedInUser.id!),
    mutationFn: () => {
      const formData = new FormData();
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      formData.append("email", email);

      if (!!logo) {
        formData.append("profile_picture", logo);
        formData.append("profile_picture320", logo);
      }

      return client.patch(USER_KEYS.mUser(loggedInUser.id!)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(selectedCompanyId!)});
      refetchUser();
      setEditCompanyMemberModal({isVisible: false, isRegistering: false});
    },
  });

  return (
    <div className="flex flex-col gap-14">
      <div className="flex flex-col items-center gap-5">
        <span className="text-neutral-black text-xl font-semibold">{t("add_profile_photo")}</span>
        <UserAvatar defaultAvatar={loggedInUser.profile_picture} handleUpload={setLogo} />
        <div className="flex flex-col items-center gap-3 self-stretch">
          {!isAgency && !isRequestPermissionFormVisible && (
            <span className="text-neutral-grey-dark text-sm font-medium">{t("permission_type")}</span>
          )}
          {!isAgency && !isRequestPermissionFormVisible && (
            <div className="border-primary-light-solid text-neutral-black w-fit self-center rounded-md border px-4 py-2 text-sm">
              {t(
                `${(currentPermission === "REFLUENCED_COMPANY_ADMIN" ? "ADMIN" : currentPermission).toLowerCase()}_permissions`,
              )}
            </div>
          )}
          {!editCompanyMemberModal.isRegistering && (
            <Can not I="create" a="Campaign">
              {isRequestPermissionFormVisible ? (
                <div className="flex w-full flex-1 flex-col gap-5 self-stretch">
                  <div className="flex flex-col gap-1">
                    <span className="text-neutral-black text-xs">{t("permission_needed")}</span>
                    <PermissionSelect
                      value={permissionToRequest}
                      onChange={value => setPermissionToRequest(value as TPermission)}
                      placeholder={t("permission_select_placeholder")}
                      excludedPermissions={excludedPermissions}
                      containerClassName="md:max-w-[auto] [&_button]:h-auto [&_button]:min-h-[60px] [&_button]:px-3 [&_button]:py-2 [&_button>span]:text-base"
                      dropdownClassName="translate-y-16"
                      dropDownIconVariant="FILLED"
                    />
                  </div>
                  <div className="flex items-center gap-5">
                    <Button
                      buttonStyle="White"
                      className="flex-1"
                      onClick={() => {
                        setPermissionToRequest(null);
                        setIsRequestPermissionFormVisible(false);
                      }}>
                      {t("cancel")}
                    </Button>
                    <Button
                      onClick={() => {
                        requestPermissionUpgrade({
                          newPermission: permissionToRequest!,
                        });
                      }}
                      isDisabled={!permissionToRequest || isRequestingPermissionUpgrade}
                      isLoading={isRequestingPermissionUpgrade}
                      buttonStyle="Gradient"
                      className="flex-1">
                      {t("request")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  buttonStyle="Blue"
                  className="h-auto px-4 py-1"
                  onClick={() => setIsRequestPermissionFormVisible(true)}>
                  {t("request_more_permissions")}
                </Button>
              )}
            </Can>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <CredentialsStep showCompanyName={false} className="gap-3" />
        <Button
          onClick={() => {
            if (validateCredentials()) {
              if (editCompanyMemberModal.isRegistering) {
                register();
              } else {
                updateUser();
              }
            }
          }}
          isLoading={isRegistering || isLoggingIn || isUpdatingUser}
          isDisabled={
            !email ||
            !firstName ||
            !lastName ||
            (!password && editCompanyMemberModal.isRegistering) ||
            isRegistering ||
            isLoggingIn ||
            isUpdatingUser
          }
          buttonStyle="Gradient"
          className="w-fit px-8 py-4">
          {t(editCompanyMemberModal.isRegistering ? "create_account" : "save_changes")}
        </Button>
      </div>
    </div>
  );
};

export default AccountEdit;
